import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Stack,
  StackProps,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import {
  IcArrowDown,
  IcArrowUp,
  IcCheckVerified,
  IcClock,
  IcEdit,
  IcEmergencyManagement,
  IcFire,
  IcMedicalCross,
  IcMunicipalServices,
  IcPolice,
} from '@/assets/images';
import { DATE_TIME_FORMAT } from '@/config';
import { useCFSContext } from '@/hooks';
import useExpandedItem from '@/hooks/useExpandedUnitItem';
import {
  AgencyType,
  IAgency,
  IUnitShiftResource,
  IUnitShiftResources,
  IUnitType,
} from '@/models';
import { canModifyCFS, getSectorNameInfo } from '@/services';
import { useAgencyStore, useDataStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

import { UnitItemDetail } from './UnitItemDetail';
import { unitShiftViewItems } from '../data';
import { UnitItemRoot } from '../styles';
import UnitResourceReallocateModal from './UnitResourceReallocateModal';

interface UnitItemProps extends PropsWithChildren {
  _id: string;
  agency: IAgency | undefined;
  itemType?: string;
  name: string;
  type: IUnitType;
  cfsNumber?: string;
  isDispatchedToHQ?: boolean;
  headquarterName?: string | null;
  hideDetail?: boolean;
  showResources?: boolean;
  resources?: IUnitShiftResources;
  rootProps?: StackProps;
  distance?: number;
  startAt?: string;
  addressLabel?: string;
  showReallocateButton?: boolean;
  onReallocateClick?: () => void;
  unitAgency?: string;
}

export const UnitItem: FC<UnitItemProps> = (props) => {
  const {
    _id,
    itemType,
    name,
    type,
    cfsNumber,
    isDispatchedToHQ,
    headquarterName,
    resources,
    hideDetail,
    showResources,
    rootProps,
    distance,
    startAt,
    addressLabel,
    showReallocateButton,
    onReallocateClick,
    unitAgency,
  } = props;
  const { beats } = useDataStore();
  const { cfs } = useCFSContext();
  const { agencies } = useAgencyStore();
  const { user } = useUserStore();
  const canModify = canModifyCFS(user);

  const { isExpanded, setExpanded: setIsExpanded } = useExpandedItem(_id);

  const theme = useTheme();

  const [isReallocModalOpen, setReallocModalOpen] = useState(false);

  const openReallocModal = () => {
    setReallocModalOpen(true);
  };

  const polygons = resources?.polygons || [];
  const { isCoverCFS, sectorNames } = getSectorNameInfo(
    beats,
    cfs?.addressInfo?.address?.point,
    polygons,
    itemType === 'on-duty',
  );

  const getIcon = useMemo(() => {
    if (type?.agencyType === AgencyType.POLICE)
      return <IcPolice color={colors.grey[80]} style={{ marginTop: -1 }} />;
    if (type?.agencyType === AgencyType.EMS) return <IcMedicalCross />;
    if (type?.agencyType === AgencyType.EMERGENCY_MANAGEMENT)
      return (
        <IcEmergencyManagement
          color={colors.grey[80]}
          style={{ marginTop: -1 }}
        />
      );
    if (type?.agencyType === AgencyType.MUNICIPAL_SERVICES)
      return (
        <IcMunicipalServices
          color={colors.grey[80]}
          style={{ marginTop: -1 }}
        />
      );
    return <IcFire color={colors.grey[80]} style={{ marginTop: -3 }} />;
  }, [type]);

  const getResourceItems = useCallback(() => {
    return unitShiftViewItems.reduce((total: any[], current) => {
      const currentResourceName = current.name as keyof typeof resources;
      if (
        resources &&
        resources[currentResourceName] &&
        (resources[currentResourceName] as IUnitShiftResource[]).length
      ) {
        const resourceItems = (
          resources[currentResourceName] as IUnitShiftResource[]
        ).map((resourceItem) => {
          const _resourceItem = resourceItem as IUnitShiftResource;
          if (currentResourceName !== 'users') return _resourceItem.name;

          const firstName = _resourceItem.firstName ?? '';
          const lastName = _resourceItem.lastName ?? '';
          const employeeName = formatPersonName({ firstName, lastName });
          const badgeNumber =
            _resourceItem.profile?.employmentInformation?.badgeNumber;

          if (badgeNumber && employeeName)
            return `${badgeNumber} ${employeeName}`;
          if (badgeNumber) return badgeNumber;
          return employeeName;
        });
        total.push({
          ...current,
          items: resourceItems,
        });
      }
      return total;
    }, []);
  }, [resources]);

  const agencyAbbreviation = useMemo(() => {
    if (unitAgency && agencies.length) {
      return agencies.find((ag) => ag._id === unitAgency)?.abbreviation ?? null;
    }
    return null;
  }, [unitAgency, agencies]);

  const isAgencyPartOfCFS = useMemo(() => {
    return unitAgency && cfs?.assignedAgencies?.includes(unitAgency);
  }, [unitAgency, cfs?.assignedAgencies]);

  const agencyAbbreviationColor = useMemo(() => {
    if (unitAgency) {
      return isAgencyPartOfCFS
        ? theme.palette.mode === 'dark'
          ? '#28485A'
          : '#cde7f7'
        : theme.palette.mode === 'dark'
        ? '#3E5A6A'
        : theme.palette.background.formHeader;
    }
    return theme.palette.background.formHeader;
  }, [
    unitAgency,
    isAgencyPartOfCFS,
    theme.palette.background.formHeader,
    theme.palette.mode,
  ]);

  return (
    <UnitItemRoot {...rootProps}>
      <Stack className="content-section" alignItems="center" marginBottom="8px">
        {isCoverCFS && <IcCheckVerified style={{ marginRight: 5 }} />}
        <Typography
          variant="button"
          color={theme.palette.text.primary}
          style={{ textTransform: 'none' }}
        >
          {name}
        </Typography>
        {startAt && (
          <Box className="name-box">
            <IcClock color={colors.primary.main} />
            <Typography variant="subtitle2" color={colors.grey[80]}>
              {moment(startAt).format(DATE_TIME_FORMAT)}
            </Typography>
          </Box>
        )}
        {isDispatchedToHQ && (
          <Box className="name-box">
            <Typography variant="subtitle2">
              {headquarterName || 'HQ'}
            </Typography>
          </Box>
        )}
        <div style={{ flex: 1 }} />
        {props.children}
      </Stack>
      <Stack sx={{ flexDirection: 'row' }}>
        <Box className="name-box">
          {getIcon}
          <Typography variant="body2" color={theme.palette.text.secondary}>
            {type?.name}
          </Typography>
        </Box>
        {agencyAbbreviation && unitAgency ? (
          <Box
            className="name-box"
            sx={{
              backgroundColor: `${agencyAbbreviationColor} !important`,
            }}
          >
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {agencyAbbreviation}
            </Typography>
          </Box>
        ) : null}
      </Stack>
      {(!hideDetail || showResources) && <Divider sx={{ my: '15px' }} />}
      {!hideDetail && !showResources && (
        <UnitItemDetail
          cfsNumber={cfsNumber}
          sectorNames={sectorNames.join(', ')}
          addressLabel={addressLabel || ''}
          distance={distance}
        />
      )}
      {showResources && (
        <Accordion expanded={isExpanded}>
          <AccordionSummary onClick={() => setIsExpanded(!isExpanded)}>
            <UnitItemDetail
              cfsNumber={cfsNumber}
              sectorNames={sectorNames.join(', ')}
              addressLabel={addressLabel || ''}
              distance={distance}
            />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              sx={{ flex: 0, padding: 0 }}
            >
              {isExpanded ? <IcArrowUp /> : <IcArrowDown />}
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <Divider sx={{ my: '15px' }} />
            <Stack flexDirection="row">
              {getResourceItems().map((resource) => (
                <Stack
                  key={resource.name}
                  className="resource-item"
                  sx={{ ml: 0, alignItems: 'center' }}
                >
                  <resource.icon style={{ margin: 0, paddingRight: '5px' }} />
                  <Stack>
                    {resource.items.map(
                      (resourceItem: string, index: number) => (
                        <Typography key={index} className="resource-name">
                          {resourceItem}
                        </Typography>
                      ),
                    )}
                  </Stack>
                </Stack>
              ))}
              {canModify && (showReallocateButton || onReallocateClick) && (
                <IconButton
                  sx={{ ml: 'auto' }}
                  onClick={onReallocateClick ?? openReallocModal}
                >
                  <IcEdit />
                </IconButton>
              )}
            </Stack>
          </AccordionDetails>
          {showReallocateButton && !onReallocateClick && (
            <UnitResourceReallocateModal
              open={isReallocModalOpen}
              onClose={() => setReallocModalOpen(false)}
              unitId={_id}
              unitName={name}
              unitType={type}
              currentResources={{
                users: resources?.users,
                vehicles: resources?.vehicles,
                equipments: resources?.equipments,
              }}
            />
          )}
        </Accordion>
      )}
    </UnitItemRoot>
  );
};
